/**
 * The one signal for "the session is gone and cannot be recovered".
 *
 * Raised by the response interceptor when a refresh fails: the refresh cookie has expired
 * (QA measures it at 600s), been revoked, or was never there. `tokenStore.clear()` already
 * drops the access token, but the token store is cleared on an ordinary logout too, so it
 * cannot carry this meaning — a subscriber must be able to tell "the admin left" from "the
 * admin was thrown out". Kept apart for the same reason `setResetToken` does not `notify()`.
 *
 * Imperative and unsubscribed in the same shape as `token-store.ts`, so `lib` needs no
 * knowledge of React and the provider that owns auth state can subscribe to it directly
 * (`providers` → `lib` is an allowed edge; `lib` → `providers` is not).
 */

/** Why the session died, when the failed refresh carried a real backend envelope (ADO 118305) —
 *  e.g. a deactivated account's `ACCOUNT_DEACTIVATED` — so a subscriber can show the server's own
 *  reason instead of the generic expiry copy. `undefined` for an ordinary expired refresh cookie,
 *  which carries no such envelope. */
export type SessionExpiryReason = { code?: string; message?: string };

const listeners = new Set<(reason?: SessionExpiryReason) => void>();

export const sessionExpiry = {
  /** Announce that the session is unrecoverable. Safe to call more than once. */
  expire: (reason?: SessionExpiryReason): void => {
    // Copy first: a listener that unsubscribes during the walk must not mutate the live set.
    [...listeners].forEach((listener) => listener(reason));
  },
  subscribe: (listener: (reason?: SessionExpiryReason) => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
